/**
 * BXGX Offer Bar — message + tick progress bar driven by cart total quantity.
 *
 * Each <bxgx-offer-bar> is self-contained: its config is embedded as JSON.
 * It listens to `cart:updated` events fired elsewhere in the theme (see
 * assets/custom.js fireCartEvents) and re-computes an "eligible" quantity
 * that excludes line items whose product carries any of the configured
 * excluded tags. Tags are fetched lazily via /products/{handle}.js and
 * cached per page load to avoid duplicate requests.
 */
(function () {
  if (window.customElements && window.customElements.get('bxgx-offer-bar')) {
    return;
  }

  // Shared cache so multiple bar instances on the same page (cart drawer +
  // product page) don't refetch the same product tags.
  var productTagCache = {};

  function normalizeTagList(value) {
    if (!value) return [];
    var arr = [];
    if (Array.isArray(value)) {
      arr = value;
    } else if (typeof value === 'string') {
      arr = value.split(/[,\n]/);
    }
    return arr
      .map(function (t) { return String(t || '').trim().toLowerCase(); })
      .filter(Boolean);
  }

  function fetchProductTags(item) {
    var handle = item && (item.handle || (item.product && item.product.handle));
    if (!handle) return Promise.resolve([]);

    if (!productTagCache[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productTagCache[handle] = fetch(root + 'products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('product fetch failed');
          return response.json();
        })
        .then(function (product) { return normalizeTagList(product && product.tags); })
        .catch(function () { return []; });
    }
    return productTagCache[handle];
  }

  var BxgxOfferBar = (function () {
    function BxgxOfferBar() {
      return Reflect.construct(HTMLElement, [], BxgxOfferBar);
    }

    BxgxOfferBar.prototype = Object.create(HTMLElement.prototype);
    BxgxOfferBar.prototype.constructor = BxgxOfferBar;
    Object.setPrototypeOf(BxgxOfferBar, HTMLElement);

    BxgxOfferBar.prototype.connectedCallback = function () {
      this.config = this._readConfig();
      if (!this.config || !this.config.milestones || this.config.milestones.length === 0) {
        this.remove();
        return;
      }

      // Sort by qty ascending so positions/active calculations are predictable.
      this.config.milestones.sort(function (a, b) {
        return (a.qty || 0) - (b.qty || 0);
      });

      // Excluded tags can come from either the embedded config (block-rendered
      // instance) or the global the cart-drawer instance publishes.
      this.excludedTags = normalizeTagList(this.config.excluded_tags);
      if (!this.excludedTags.length && window.BxgxOfferGlobal && window.BxgxOfferGlobal.excludedTags) {
        this.excludedTags = normalizeTagList(window.BxgxOfferGlobal.excludedTags);
      }

      this.bannerEl = this.querySelector('[data-bxgx-banner]');
      this.fillEl = this.querySelector('[data-bxgx-fill]');
      this.ticksEl = this.querySelector('[data-bxgx-ticks]');

      this._renderTicks();

      // First paint policy:
      // - PDP instance has no block context, so Liquid couldn't apply
      //   exclusion (server_exclusion_applied=false). If exclusions are
      //   configured globally, trusting the server count would flash the
      //   wrong message until /cart.js hydration finishes. Stay hidden.
      // - Cart-drawer instance has block context, so the server-side count
      //   is already correct — paint immediately.
      // - When no exclusions are configured, server count is always fine.
      var hasExclusions = this.excludedTags.length > 0;
      var serverApplied = this.config.server_exclusion_applied === true;
      var trustServerCount = !hasExclusions || serverApplied;

      if (trustServerCount) {
        var initialQty = (typeof this.config.cart_eligible_qty === 'number')
          ? this.config.cart_eligible_qty
          : (this.config.cart_item_count || 0);
        this._update(initialQty);
      } else {
        // Keep wrapper hidden — hydration below will _update with the right qty.
        this.hidden = true;
      }

      var self = this;
      this._onCartUpdated = function (event) {
        var cart = event && event.detail && event.detail.cart;
        if (!cart) return;
        self._calculateEligibleQty(cart).then(function (qty) {
          self._update(qty);
        });
      };
      document.documentElement.addEventListener('cart:updated', this._onCartUpdated);

      // Hydrate from /cart.js on connect when exclusions are active. This
      // covers two cases:
      //   1. Product-page bar — Liquid had no block, so server-side
      //      cart_eligible_qty equals raw item_count (no exclusion applied).
      //   2. Section was re-rendered while items were already in the cart;
      //      the new instance otherwise wouldn't recompute until the next
      //      cart:updated event fires.
      if (this.excludedTags.length) {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'cart.js', { credentials: 'same-origin' })
          .then(function (response) { return response.json(); })
          .then(function (cart) {
            return self._calculateEligibleQty(cart).then(function (qty) {
              self._update(qty);
            });
          })
          .catch(function () { /* leave initial render in place */ });
      }
    };

    BxgxOfferBar.prototype.disconnectedCallback = function () {
      if (this._onCartUpdated) {
        document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
      }
    };

    BxgxOfferBar.prototype._readConfig = function () {
      var script = this.querySelector('script[type="application/json"]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error('[bxgx-offer-bar] invalid JSON config', e);
        return null;
      }
    };

    // Check if a cart item is a true freebie (not a BOGO discount item).
    // Freebies have original_price of 0 or ≤ 100 (₹1) with no discount allocations.
    // BOGO items have real original_price but final_price = 0 due to discount.
    function isFreeGiftItem(item) {
      return item.original_price <= 100 &&
        (!item.discount_allocations || item.discount_allocations.length === 0);
    }

    BxgxOfferBar.prototype._calculateEligibleQty = function (cart) {
      if (!cart || !Array.isArray(cart.items)) return Promise.resolve(0);

      // No exclusions configured — fast path: count minus free gift items.
      if (!this.excludedTags || !this.excludedTags.length) {
        var freeQty = 0;
        cart.items.forEach(function (item) {
          if (isFreeGiftItem(item)) {
            freeQty += item.quantity || 0;
          }
        });
        var fast = (typeof cart.item_count === 'number' ? cart.item_count : parseInt(cart.item_count, 10) || 0) - freeQty;
        return Promise.resolve(fast);
      }

      var excluded = this.excludedTags;
      var promises = cart.items.map(function (item) {
        // Skip true free gift items (not BOGO discount items)
        if (isFreeGiftItem(item)) {
          return Promise.resolve(0);
        }
        return fetchProductTags(item).then(function (tags) {
          var isExcluded = tags.some(function (tag) { return excluded.indexOf(tag) !== -1; });
          return isExcluded ? 0 : (item.quantity || 0);
        });
      });

      return Promise.all(promises).then(function (counts) {
        return counts.reduce(function (sum, n) { return sum + n; }, 0);
      });
    };

    BxgxOfferBar.prototype._positionForQty = function (qty) {
      var milestones = this.config.milestones;
      var minQty = milestones[0].qty;
      var maxQty = milestones[milestones.length - 1].qty;
      var start = 5;
      var end = 95;

      if (milestones.length <= 1 || maxQty === minQty) {
        return end;
      }

      var ratio = (qty - minQty) / (maxQty - minQty);
      return start + ratio * (end - start);
    };

    BxgxOfferBar.prototype._renderTicks = function () {
      if (!this.ticksEl) return;
      this.ticksEl.innerHTML = '';

      var self = this;
      this.config.milestones.forEach(function (milestone, index) {
        var tick = document.createElement('div');
        tick.className = 'bxgx-offer__tick' + (milestone.highlight ? ' is-highlight' : '');
        tick.style.left = self._positionForQty(milestone.qty) + '%';
        tick.dataset.qty = String(milestone.qty);
        tick.dataset.index = String(index);

        var dot = document.createElement('span');
        dot.className = 'bxgx-offer__dot';
        dot.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.2l3 3 6-6.4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        tick.appendChild(dot);

        if (milestone.label) {
          var label = document.createElement('span');
          label.className = 'bxgx-offer__label';
          label.textContent = milestone.label;
          tick.appendChild(label);
        }

        self.ticksEl.appendChild(tick);
      });
    };

    BxgxOfferBar.prototype._update = function (cartQty) {
      var milestones = this.config.milestones;
      var minQty = milestones[0].qty;
      var maxQty = milestones[milestones.length - 1].qty;
      var hideEmpty = this.dataset.hideWhenEmpty !== 'false';

      // Below the smallest milestone — hide the whole component (per spec).
      if (hideEmpty && cartQty < minQty) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      // Fill bar — proportional to cart qty, clamped to the bar's tick range.
      var fillPct = 0;
      if (cartQty >= maxQty) {
        fillPct = 95;
      } else if (cartQty <= minQty) {
        fillPct = 5;
      } else {
        fillPct = this._positionForQty(cartQty);
      }
      if (this.fillEl) {
        this.fillEl.style.width = fillPct + '%';
      }

      // Tick states.
      var ticks = this.ticksEl ? this.ticksEl.querySelectorAll('.bxgx-offer__tick') : [];
      ticks.forEach(function (tick) {
        var qty = parseInt(tick.dataset.qty, 10) || 0;
        tick.classList.toggle('is-active', cartQty >= qty);
        tick.classList.toggle('is-matched', cartQty === qty);
      });

      // Banner — exact-match message wins; otherwise show the message of the
      // last milestone whose qty <= cartQty so users see context for what
      // they've already unlocked. Hide if we have nothing meaningful to say.
      var matched = null;
      var lastReached = null;
      for (var i = 0; i < milestones.length; i++) {
        var m = milestones[i];
        if (m.qty === cartQty && m.message) {
          matched = m;
        }
        if (m.qty <= cartQty && m.message) {
          lastReached = m;
        }
      }
      var bannerMessage = matched ? matched.message : (lastReached ? lastReached.message : '');
      if (this.bannerEl) {
        if (bannerMessage) {
          this.bannerEl.textContent = bannerMessage;
          this.bannerEl.hidden = false;
        } else {
          this.bannerEl.textContent = '';
          this.bannerEl.hidden = true;
        }
      }
    };

    return BxgxOfferBar;
  })();

  window.customElements.define('bxgx-offer-bar', BxgxOfferBar);
})();
